import React, { Dispatch, SetStateAction } from "react";
import { Grid, TextField, MenuItem, Button } from "@mui/material";

interface Props {
  students: Array<{ id: string; admissionNumber: string; scholarNumber?: string | null; firstName: string; lastName: string; class?: { name: string }; section?: { name: string } }>;
  templates: Array<{ id: string; name: string }>;
  requiredFields?: Array<{ key: string; label: string; type: string; options?: string[] }>;
  form: { studentId: string; templateId: string; issueDate: string; remarks: string; extraFields?: Record<string, string> };
  onChange: Dispatch<SetStateAction<{ studentId: string; templateId: string; issueDate: string; remarks: string; extraFields: Record<string, string> }>>;
  onPreview: () => void;
  onGenerate: () => void;
  onReset: () => void;
  onHistory: () => void;
}

const CertificateForm: React.FC<Props> = ({ students, templates, requiredFields = [], form, onChange, onPreview, onGenerate, onReset, onHistory }) => {
  const f = (name: string, value: any) => onChange((prev) => ({ ...prev, [name]: value }));
  const fx = (key: string, value: any) =>
    onChange((prev) => ({ ...prev, extraFields: { ...(prev.extraFields || {}), [key]: value } }));

  // Check if selected template is STUDENT_ID
  const selectedTemplate = templates.find(t => t.id === form.templateId);
  const isStudentId = (selectedTemplate as any)?.code === "STUDENT_ID";

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          label="Student"
          value={form.studentId}
          onChange={(e) => f("studentId", e.target.value)}
        >
          {students.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.admissionNumber} — {s.firstName} {s.lastName} ({s.class?.name || ""} {s.section?.name || ""})
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField select fullWidth label="Certificate Type" value={form.templateId} onChange={(e) => f("templateId", e.target.value)}>
          {templates.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Issue Date"
          type="date"
          fullWidth
          value={form.issueDate}
          onChange={(e) => f("issueDate", e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField label="Remarks" fullWidth value={form.remarks} onChange={(e) => f("remarks", e.target.value)} />
      </Grid>
      {requiredFields.map((rf) => {
        // Hide these fields if STUDENT_ID template is selected as they are auto-fetched
        if (isStudentId && ['photo', 'scholarno', 'class', 'dob', 'validupto', 'bloodgroup'].includes(rf.key.toLowerCase().replace(/[^a-z]/g, ''))) {
            return null;
        }

        return (
        <Grid item xs={12} md={4} key={rf.key}>
          {rf.type === "select" ? (
            <TextField
              select
              fullWidth
              label={rf.label}
              value={(form.extraFields || {})[rf.key] || ""}
              onChange={(e) => fx(rf.key, e.target.value)}
            >
              {(rf.options || []).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              fullWidth
              label={rf.label}
              type={rf.type === "date" ? "date" : "text"}
              InputLabelProps={rf.type === "date" ? { shrink: true } : undefined}
              value={(form.extraFields || {})[rf.key] || ""}
              onChange={(e) => fx(rf.key, e.target.value)}
            />
          )}
        </Grid>
        );
      })}
      <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
        <Button variant="outlined" onClick={onPreview}>
          Preview
        </Button>
        <Button variant="contained" onClick={onGenerate}>
          Generate Certificate
        </Button>
        <Button variant="outlined" color="secondary" onClick={onHistory}>
          History
        </Button>
        <Button onClick={onReset}>Reset</Button>
      </Grid>
    </Grid>
  );
};

export default CertificateForm;
